import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, tap } from 'rxjs';

import { RestProjectTypeModel } from '@main-application/management/pages/service-configuration/project-types/project-types.interface';
import { CreateDynamicTicketTypeModel } from '@shared/enums/ticket-type';
import { RestUserModel } from '@shared/interfaces/user.interface';

import { RestapiService } from './restapi.service';

export interface DynamicTicketType {
  id: number;
  dateCreated: Date;
  dateUpdated: Date;
  lastModifiedById: number;
  createdById: number;
  createdBy: RestUserModel;
  lastModifiedBy: RestUserModel;
  name: string;
  notifyAssignee: number;
  notifyAssigneeOnCustom: number;
  remindAssigneeEveryNDays: number;
  remindAssigneeStopAfterNReminders: number;
  builtInType: number;
}

@Injectable({
  providedIn: 'root',
})
export class DynamicTicketTypeService {
  constructor(private restApiService: RestapiService) {}

  cachedTickets$ = new BehaviorSubject<DynamicTicketType[]>([]);

  getDynamicTicketTypes(): Observable<DynamicTicketType[]> {
    return this.restApiService.getData<DynamicTicketType[]>(`DynamicTicketType`).pipe(
      tap(tickets => {
        this.cachedTickets$.next(tickets);
      })
    );
  }

  getCachedDynamicTicketTypes(): Observable<DynamicTicketType[]> {
    if (this.cachedTickets$.getValue().length) {
      return this.cachedTickets$.asObservable();
    }
    return this.getDynamicTicketTypes();
  }

  createDynamicTicketType(item: CreateDynamicTicketTypeModel): Observable<DynamicTicketType> {
    this.resetTicketsCache();
    return this.restApiService.create<DynamicTicketType>(`DynamicTicketType`, item);
  }

  editDynamicTicket(item: CreateDynamicTicketTypeModel, id: number): Observable<DynamicTicketType> {
    this.resetTicketsCache();
    return this.restApiService.update<DynamicTicketType>(`DynamicTicketType/${id}`, item);
  }

  deleteDynamicTicketType(id: number): Observable<void> {
    this.resetTicketsCache();
    return this.restApiService.delete(`DynamicTicketType/${id}`);
  }

  getDynamicTicketById(id: number): Observable<DynamicTicketType> {
    return this.restApiService.getData<DynamicTicketType>(`DynamicTicketType/${id}`);
  }

  getProjectTypes(): Observable<RestProjectTypeModel[]> {
    return this.restApiService.getData<RestProjectTypeModel[]>(`adminPanel/projectTypes`);
  }

  getProjectTypeById(id: number): Observable<RestProjectTypeModel> {
    return this.restApiService.getData<RestProjectTypeModel>(`adminPanel/projectTypes/${id}`);
  }

  createProjectType(item: { name: string }): Observable<RestProjectTypeModel> {
    return this.restApiService.create<RestProjectTypeModel>(`adminPanel/projectTypes`, item);
  }

  editProjectType(item: { name: string }, id: number): Observable<RestProjectTypeModel> {
    return this.restApiService.update<RestProjectTypeModel>(`adminPanel/projectTypes/${id}`, item);
  }

  deleteProjectType(id: number): Observable<void> {
    return this.restApiService.delete(`adminPanel/projectTypes/${id}`);
  }

  resetTicketsCache(): void {
    this.cachedTickets$.next([]);
  }
}
